import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { getKpiStats, IGetKpiStatsResponse } from "@/machines/api/requests/machines.requests.ts";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import { Machine } from "@/machines/types/machine.ts";
import { MachineStats } from "@/machines/types/machine-stats.ts";

export interface UseAggregatedKpisParams {
  deviceMxIds: Array<Machine["device_mxid"]>;
  start: Date;
  end: Date;
}

export interface AggregatedKpisResult {
  aggregated: MachineStats;
  perMachine: Array<{ device_mxid?: string; stats: IGetKpiStatsResponse | null }>;
}

export const useAggregatedKpis = ({ deviceMxIds, start, end }: UseAggregatedKpisParams) => {
  const queries = useQueries({
    queries: (deviceMxIds ?? []).map((mxid) => ({
      queryKey: [QUERY_KEYS.KPI_STATS, { device_mxid: mxid, start_time: start, end_time: end }],
      queryFn: () => getKpiStats({ device_mxid: mxid, start_time: start, end_time: end }),
      enabled: !!mxid && !!start && !!end,
      staleTime:  1000 * 60 * 60 * 12,
    })),
  });

  const isLoading = queries.some((q) => q.status === "pending");

  const result = useMemo<AggregatedKpisResult | null>(() => {
    const responses = queries
      .map((q, idx) => ({
        device_mxid: deviceMxIds[idx],
        stats: q.data?.data ?? null,
      }));

    const valid = responses.filter((r) => !!r.stats) as Array<{ device_mxid?: string; stats: IGetKpiStatsResponse }>;
    const count = valid.length || 1;

    // Initialize accumulators
    let sumAvgDumpsPerTruck = 0;
    let sumAvgDurationSeconds = 0;
    let sumAvgTimeBetweenTrucks = 0;
    let sumDurationStd = 0;
    let sumEfficiency = 0;
    let sumTotalSamples = 0;
    let sumTruckEventCount = 0;
    let maxUptime: Date | null = null;

    valid.forEach(({ stats }) => {
      const cur = stats.current;
      if (!cur) return;
      sumAvgDumpsPerTruck += cur.avgDumpsPerTruck ?? 0;
      sumAvgDurationSeconds += cur.avgDurationSeconds ?? 0;
      sumAvgTimeBetweenTrucks += cur.avgTimeBetweenTrucks ?? 0;
      sumDurationStd += cur.durationStd ?? 0;
      sumEfficiency += cur.efficiency ?? 0;
      sumTotalSamples += cur.totalSamples ?? 0;
      sumTruckEventCount += cur.truckEventCount ?? 0;
      if (cur.uptime) {
        const up = new Date(cur.uptime);
        if (!maxUptime || up > maxUptime) maxUptime = up;
      }
    });

    const aggregated: MachineStats = {
      avgDumpsPerTruck: sumAvgDumpsPerTruck / count,
      avgDurationSeconds: sumAvgDurationSeconds / count,
      avgTimeBetweenTrucks: sumAvgTimeBetweenTrucks / count,
      durationStd: sumDurationStd / count,
      efficiency: sumEfficiency / count,
      totalSamples: sumTotalSamples, // total
      truckEventCount: sumTruckEventCount, // total
      uptime: maxUptime ?? new Date(0),
    };

    return {
      aggregated,
      perMachine: responses,
    };
  }, [queries, deviceMxIds]);

  return { isLoading, data: result };
};


